<template>
    <div class="member-pagination-wrapper">
        <nav class="member-pagination" aria-label="Pagination">
            <!-- First Page Button -->
            <button 
                class="pagination-btn pagination-btn--edge"
                :class="{ 'pagination-btn--disabled': currentPage <= 1 }"
                :disabled="currentPage <= 1"
                @click="goToPage(1)"
                aria-label="Go to first page"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="11 17 6 12 11 7"></polyline>
                    <polyline points="18 17 13 12 18 7"></polyline>
                </svg>
            </button>

            <!-- Previous Page Button -->
            <button 
                class="pagination-btn pagination-btn--nav"
                :class="{ 'pagination-btn--disabled': currentPage <= 1 }"
                :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)"
                aria-label="Go to previous page"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>

            <!-- Page Numbers -->
            <div class="pagination-pages">
                <button
                    v-for="pageNum in renderList"
                    :key="pageNum"
                    class="pagination-btn pagination-btn--page"
                    :class="{ 'pagination-btn--active': currentPage === pageNum }"
                    @click="goToPage(pageNum)"
                    :aria-label="`Go to page ${pageNum}`"
                    :aria-current="currentPage === pageNum ? 'page' : undefined"
                >
                    {{ pageNum }}
                </button>
            </div>

            <!-- Next Page Button -->
            <button 
                class="pagination-btn pagination-btn--nav"
                :class="{ 'pagination-btn--disabled': currentPage >= totalPages }"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
                aria-label="Go to next page"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>

            <!-- Last Page Button -->
            <button 
                class="pagination-btn pagination-btn--edge"
                :class="{ 'pagination-btn--disabled': currentPage >= totalPages }"
                :disabled="currentPage >= totalPages"
                @click="goToPage(totalPages)"
                aria-label="Go to last page"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="13 17 18 12 13 7"></polyline>
                    <polyline points="6 17 11 12 6 7"></polyline>
                </svg>
            </button>
        </nav>
    </div>
</template>

<script>
export default {
    name: 'MemberPagination',
    props: {
        page: {
            type: [Number, String],
            required: true
        },
        total: {
            type: [Number, String],
            required: true
        }
    },
    emits: ['callback'],
    data() {
        return {
            currentPage: 1
        };
    },
    computed: {
        totalPages() {
            return typeof this.total === 'string' ? parseInt(this.total, 10) : this.total;
        },
        renderList() {
            const pages = [];
            const range = 5;
            const total = this.totalPages;

            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }

            if (pages.length > range) {
                const halfRange = Math.floor(range / 2);
                let start = this.currentPage - halfRange - 1;
                let end = this.currentPage + halfRange;

                if (start < 0) {
                    start = 0;
                    end = Math.min(range, pages.length);
                } else if (end > pages.length) {
                    end = pages.length;
                    start = Math.max(0, pages.length - range);
                }

                return pages.slice(start, end);
            }

            return pages;
        }
    },
    watch: {
        page: {
            handler(newPage) {
                const pageNumber = typeof newPage === 'string' ? parseInt(newPage, 10) : newPage;
                this.currentPage = pageNumber;
            },
            immediate: true
        }
    },
    methods: {
        goToPage(pageNum) {
            if (pageNum < 1 || pageNum > this.totalPages || pageNum === this.currentPage) {
                return;
            }
            this.currentPage = pageNum;
            this.$emit('callback', pageNum);
        }
    },
    mounted() {
        const pageNumber = typeof this.page === 'string' ? parseInt(this.page, 10) : this.page;
        this.currentPage = pageNumber;
    }
};
</script>

<style scoped>
.member-pagination-wrapper {
    display: flex;
    justify-content: center;
    padding: 24px 0;
    margin-top: auto; /* Push to bottom of flex container */
    flex-shrink: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.member-pagination {
    display: flex;
    align-items: center;
    gap: 6px;
    background: white;
    padding: 8px 12px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
}

.member-pagination::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
}

.pagination-pages {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #4B5563;
    font-weight: 500;
    flex-shrink: 0;
}

.pagination-btn:focus {
    outline: none;
}

.pagination-btn:focus-visible {
    outline: 2px solid var(--primary-border, var(--primary-color, #F93E3E));
    outline-offset: 2px;
}

/* Edge buttons (first/last) */
.pagination-btn--edge {
    width: 32px;
    height: 32px;
    border-radius: 8px;
}

.pagination-btn--edge:hover:not(:disabled) {
    background: #F3F4F6;
}

/* Navigation buttons (prev/next) */
.pagination-btn--nav {
    width: 36px;
    height: 36px;
    border-radius: 8px;
}

.pagination-btn--nav:hover:not(:disabled) {
    background: #F3F4F6;
}

/* Page number buttons */
.pagination-btn--page {
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
}

.pagination-btn--page:hover:not(.pagination-btn--active) {
    background: #F3F4F6;
}

/* Active state */
.pagination-btn--active {
    background: var(--primary-color, #F93E3E);
    color: white;
}

.pagination-btn--active:hover {
    background: var(--primary-color, #F93E3E);
    filter: brightness(0.9);
}

/* Disabled state */
.pagination-btn--disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

/* =========================
   Responsive Styles
   ========================= */

@media (max-width: 991px) {
    .member-pagination-wrapper {
        padding: 20px 16px 96px 16px; /* Extra bottom padding so bottom navbar never overlaps pagination */
    }

    .member-pagination {
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }

    .pagination-btn--edge {
        width: 28px;
        height: 28px;
    }

    .pagination-btn--nav {
        width: 32px;
        height: 32px;
    }

    .pagination-btn--page {
        min-width: 32px;
        height: 32px;
        font-size: 13px;
    }
}

@media (max-width: 400px) {
    .member-pagination-wrapper {
        padding: 16px 8px;
    }

    .member-pagination {
        padding: 6px 8px;
        gap: 3px;
    }

    .pagination-pages {
        gap: 2px;
    }

    .pagination-btn--edge {
        width: 26px;
        height: 26px;
    }

    .pagination-btn--edge svg {
        width: 14px;
        height: 14px;
    }

    .pagination-btn--page {
        min-width: 26px;
        height: 26px;
        padding: 0 4px;
        font-size: 12px;
    }

    .pagination-btn--nav {
        width: 26px;
        height: 26px;
    }

    .pagination-btn--nav svg {
        width: 14px;
        height: 14px;
    }
}

@media (max-width: 360px) {
    .member-pagination {
        padding: 4px 6px;
        gap: 2px;
    }

    .pagination-pages {
        gap: 2px;
    }

    .pagination-btn--edge {
        width: 24px;
        height: 24px;
    }

    .pagination-btn--nav {
        width: 24px;
        height: 24px;
    }

    .pagination-btn--page {
        min-width: 24px;
        height: 24px;
        padding: 0 2px;
        font-size: 11px;
    }
}
</style>
